import { DatasetData } from "../model_gen/dataset";
import { GeneralService } from "./GeneralService";

export class DatasetService {
  public static getDatasets(
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByIntentGroup(
    intentGroupId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/intent_group/${intentGroupId}/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByIntent(
    applicationId: number,
    intentId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/application/${applicationId}/${intentId}/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByTestSet(
    testSetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/test_set/${testSetId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createDataset(
    payload: DatasetData,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.post(
      "/datasets",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.delete(
      `/datasets/${datasetId}`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string,
    requestTypes?: string[]
  ) {
    let url = `/datasets/${datasetId}/requests/${offset}/${limit}`;

    if (requestTypes && requestTypes.length > 0) {
      const params = requestTypes.map((type) => `request_type[]=${type}`);
      url += `?${params.join("&")}`;
    }

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEligibleDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string,
    sessionId?: string,
    requestTypes?: string[]
  ) {
    let url = `/datasets/${datasetId}/eligible_requests/${offset}/${limit}`;
    const params: string[] = [];

    if (sessionId) {
      params.push(`session_id=${sessionId}`);
    }
    if (requestTypes && requestTypes.length > 0) {
      requestTypes.forEach((type) => params.push(`request_type[]=${type}`));
    }

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static searchEligibleDatasetRequests(
    datasetId: number,
    query: string,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/eligible_requests/search?query=${encodeURIComponent(
        query
      )}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static searchAddedDatasetRequests(
    datasetId: number,
    query: string,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/added_requests/search?query=${encodeURIComponent(
        query
      )}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static addDatasetRequests(
    datasetId: number,
    payload: any,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/requests/add`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static removeDatasetRequests(
    datasetId: number,
    payload: any,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/requests/remove`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEstimatedDatasetRequestCount(
    intentGroupId: number | null,
    applicationId: number | null,
    intentId: number | null,
    dateStart: number | null,
    dateEnd: number | null,
    ignoredApplicationIds: number[] | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    let url = "/datasets/estimated_request_count?";

    if (intentGroupId) {
      url += `intent_group_id=${intentGroupId}&`;
    }

    if (applicationId) {
      url += `application_id=${applicationId}&`;
    }

    if (intentId) {
      url += `intent_id=${intentId}&`;
    }

    if (dateStart) {
      url += `date_start=${dateStart}&`;
    }

    if (dateEnd) {
      url += `date_end=${dateEnd}&`;
    }
    if (ignoredApplicationIds && ignoredApplicationIds.length > 0) {
      url += `ignored_application_ids=${ignoredApplicationIds.join(",")}&`;
    }

    // Remove trailing '&' if present
    url = url.endsWith("&") ? url.slice(0, -1) : url;

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getIntentGroupRequestCount(
    intentGroupId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/count/intent_group/${intentGroupId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getApplicationIntentRequestCount(
    applicationId: number,
    intentId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/count/application/${applicationId}/intent/${intentId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequestDistribution(
    datasetId: string | number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/request_distribution`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDatasetRequestTags(
    datasetId: string | number,
    requestId: string,
    tags: string[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/request/${requestId}/tags`,
      JSON.stringify({ tags }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRegexReplacementsApplicableCount(
    datasetId: number,
    pattern: string,
    replacement: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements/applicable_count`,
      JSON.stringify({ pattern, replacement }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static applyRegexReplacements(
    datasetId: number,
    regexReplacements: any[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements`,
      JSON.stringify({ regex_replacements: regexReplacements }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static cloneDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/clone`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static startAiReview(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string,
    payload: { correction_type: string; choices?: string[] }
  ) {
    return GeneralService.post(
      `/dataset_review/dataset/${datasetId}/review`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static getAiReviewStatus(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/status`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static augmentRequest(
    requestId: string,
    count: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string,
    datasetId: number
  ) {
    return GeneralService.post(
      `/dataset_augmentation/request/${requestId}/augment`,
      JSON.stringify({ count, dataset_id: datasetId }),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviews(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_review/dataset/${datasetId}/reviews/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReview(
    reviewId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_review/review/${reviewId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static retryDatasetReview(
    reviewId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.put(
      `/dataset_review/review/${reviewId}/retry`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviewProgressByDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_review/dataset/${datasetId}/progress`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetAugmentations(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_augmentation/dataset/${datasetId}/augmentations/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetPrompt(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/prompt`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getAugmentationRequests(
    augmentationId: string,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_augmentation/augmentation/${augmentationId}/requests/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getAugmentationDetails(
    augmentationId: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/dataset_augmentation/augmentation/${augmentationId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
